/**
 * Supabase Auth Repository
 * Handles authentication via Supabase Auth
 */

import type { IAuthRepository, User } from '../interfaces/IAuthRepository'
import { requireSupabaseClient, type ProfileRow } from '@/lib/supabase'

export class SupabaseAuthRepository implements IAuthRepository {
  /**
   * Login with email magic link
   * In real implementation, this sends a magic link email
   */
  async login(
    emailOrPhone: string
  ): Promise<{ success: boolean; user?: User }> {
    const supabase = requireSupabaseClient()

    // Determine if email or phone
    const isEmail = emailOrPhone.includes('@')

    if (isEmail) {
      const { error } = await supabase.auth.signInWithOtp({
        email: emailOrPhone,
        options: {
          emailRedirectTo: `${window.location.origin}/feed`,
        },
      })

      if (error) {
        throw new Error(`Login failed: ${error.message}`)
      }

      // Magic link sent - user needs to click it
      // Return success but no user yet (they need to verify)
      return { success: true }
    } else {
      // Phone login -- shouldCreateUser true to bypass GoTrue config check;
      // unauthorized users are still gated by is_approved + AccessGateOverlay
      const { error } = await supabase.auth.signInWithOtp({
        phone: emailOrPhone,
      })

      if (error) {
        throw new Error(`Login failed: ${error.message}`)
      }

      return { success: true }
    }
  }

  /**
   * Login with Google OAuth
   */
  async loginWithGoogle(): Promise<void> {
    const supabase = requireSupabaseClient()

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/feed`,
      },
    })

    if (error) {
      throw new Error(`Google login failed: ${error.message}`)
    }
  }

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    const supabase = requireSupabaseClient()
    const { error } = await supabase.auth.signOut()

    if (error) {
      throw new Error(`Logout failed: ${error.message}`)
    }
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    const supabase = requireSupabaseClient()
    const {
      data: { session },
    } = await supabase.auth.getSession()

    return !!session
  }

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<User | null> {
    const supabase = requireSupabaseClient()
    const {
      data: { user },
    } = await supabase.auth.getUser()

    if (!user) return null

    const { data: profile } = await supabase
      .from('profiles')
      .select('username, display_name, is_admin, is_approved')
      .eq('id', user.id)
      .single<
        Pick<
          ProfileRow,
          'username' | 'display_name' | 'is_admin' | 'is_approved'
        >
      >()

    return {
      id: user.id,
      username: profile?.username || user.email?.split('@')[0] || 'user',
      email: user.email,
      phone: user.phone,
      isAdmin: profile?.is_admin || false,
      isApproved: profile?.is_approved ?? false,
      createdAt: new Date(user.created_at),
    }
  }
}
